import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import HeroCanvas from './HeroCanvas';
import { siteData } from '../data/mock';

const MARQUEE_WORDS = [
  'Strategy',
  'AI Certification',
  'Website Design',
  'Graphic Design',
  'Media Production',
  'Brand Storytelling',
  'Growth Marketing',
];

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Doubled marquee words with stable unique keys (prevents duplicate-key warnings)
  const marqueeItems = useMemo(
    () =>
      [...MARQUEE_WORDS, ...MARQUEE_WORDS].map((word, i) => ({
        id: `${word}-${i}`,
        word,
      })),
    [],
  );

  return (
    <section id="home" className="relative min-h-screen w-full overflow-hidden bg-nebula">
      {/* Grid overlay */}
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />

      {/* 3D canvas as background */}
      <div className="absolute inset-0">
        <HeroCanvas />
      </div>

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(5,7,15,0.85) 100%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 container min-h-screen flex flex-col justify-center pt-32 md:pt-36 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-8"
        >
          <Sparkles size={16} className="text-[#d5b56b]" />
          <span className="text-xs tracking-[0.4em] uppercase text-[#eef1f8]/60">
            Est. 2002 • Marketing Built for Growth
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-6xl md:text-8xl lg:text-[9rem] tracking-tight text-[#eef1f8] leading-[1.15]"
        >
          <div>Complete</div>
          <div><span className="italic text-navy-gold hero-descender-safe font-marketing">Marketing</span></div>
          <div><span className="text-gold hero-descender-safe">Solutions.</span></div>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-[#eef1f8]/70 text-lg leading-relaxed">
            Ambitious brands don't need another vendor — they need a partner.
            We build the strategy, design, and AI-powered systems that turn
            marketing spend into measurable growth.
          </p>

          <div className="flex items-center gap-6">
            <button onClick={() => scrollTo('#contact')} className="btn-primary group">
              <span>Start Your Project</span>
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => scrollTo('#services')}
              className="text-xs tracking-[0.24em] uppercase text-[#eef1f8]/70 hover:text-[#d5b56b] transition-colors reveal-line"
            >
              See What We Do
            </button>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase text-[#eef1f8]/40">
            Scroll
          </span>
          <div className="w-px h-16 bg-gradient-to-b from-[#d5b56b] to-transparent">
            <motion.div
              animate={{ y: [0, 60, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              className="w-px h-4 bg-[#d5b56b]"
            />
          </div>
        </motion.div>
      </div>

      {/* Marquee at bottom */}
      <div className="absolute bottom-0 left-0 right-0 py-6 border-t border-[#d5b56b]/10 bg-[#05070f]/40 backdrop-blur-sm overflow-hidden z-10">
        <div className="marquee-track whitespace-nowrap">
          {marqueeItems.map(({ id, word }) => (
            <span key={id} className="font-serif text-2xl md:text-3xl italic mx-8 text-[#eef1f8]/40">
              {word} <span className="text-[#d5b56b] mx-4">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
